import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Send, CheckCircle2, MessageCircle, Clock, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", message: "" });
      toast.success("Nachricht gesendet - wir melden uns bald.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Senden fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" className="border-t border-slate-200 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-3">Kontakt</div>
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">Noch Fragen? Wir hören zu.</h2>
          <p className="mt-4 text-slate-600 text-lg leading-relaxed">
            Schreib uns direkt - egal ob zu Symptomen, dem Ernährungsplan oder einzelnen Rezepten. Wir antworten meist innerhalb von 48 Stunden.
          </p>
          <div className="mt-8 space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                <MessageCircle className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <div className="font-semibold text-slate-900">Persönliche Antwort</div>
                <div className="text-sm text-slate-500">Jede Nachricht wird von uns gelesen.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <div className="font-semibold text-slate-900">Schnelle Rückmeldung</div>
                <div className="text-sm text-slate-500">In der Regel innerhalb von 48 Stunden.</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <div className="font-semibold text-slate-900">laclive@gmx.de</div>
                <div className="text-sm text-slate-500">Oder direkt per E-Mail.</div>
              </div>
            </div>
          </div>
          <Link to="/kontakt" className="inline-block mt-8 text-sm text-emerald-700 font-medium hover:text-emerald-800" data-testid="home-contact-page-link">
            Zur ausführlichen Kontaktseite →
          </Link>
        </div>

        <div className="lg:col-span-7">
          <Card className="p-8 lg:p-10 rounded-3xl border-slate-100 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.2)]">
            {sent ? (
              <div className="text-center py-10">
                <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
                <div className="text-xl font-semibold text-slate-900">Vielen Dank!</div>
                <p className="text-slate-600 mt-2">Deine Nachricht ist bei uns angekommen.</p>
                <Button onClick={() => setSent(false)} className="mt-6 rounded-full" variant="outline" data-testid="home-contact-new">Neue Nachricht</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="home-contact-form">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="home-c-name">Name</Label>
                    <Input id="home-c-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1.5 h-11" placeholder="Vorname Nachname" data-testid="home-contact-name" />
                  </div>
                  <div>
                    <Label htmlFor="home-c-email">E-Mail</Label>
                    <Input id="home-c-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl mt-1.5 h-11" placeholder="deine@email.de" data-testid="home-contact-email" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="home-c-msg">Deine Frage oder Nachricht</Label>
                  <Textarea id="home-c-msg" required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-xl mt-1.5" placeholder="Wie können wir dir helfen?" data-testid="home-contact-message" />
                </div>
                <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
                  <p className="text-xs text-slate-500">Wir schützen deine Daten. Keine Weitergabe an Dritte.</p>
                  <Button type="submit" disabled={loading} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 px-6 btn-lift" data-testid="home-contact-submit">
                    <Send className="w-4 h-4 mr-2" /> {loading ? "Wird gesendet..." : "Nachricht senden"}
                  </Button>
                </div>
              </form>
            )}
          </Card>
        </div>
      </div>
    </section>
  );
}
